import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RichTextEditor } from '../RichTextEditor';
import { AttachmentPicker } from './AttachmentPicker';
import { HASHTAG_SUGGESTIONS } from '../../data/communications';
import { getReach } from '../../utils/audience';
import { resolvedLength } from '../../utils/variables';
import { AiTips } from './AiTips';
import { generateForChannel, getChannelTips, isChannelEmpty, type AiContext, type AiTip } from '../../utils/aiAssist';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}
function Switch({
  checked,
  onChange,
  label
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3829" data-mp="xr472tv4g6ix">
      <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3830" data-mp="et1p2z0zr9um" />
    </button>;
}
interface ChannelEditorProps {
  channels: ChannelsState;
  onToggle: (key: keyof ChannelsState) => void;
  onUpdate: <K extends keyof ChannelsState>(key: K, patch: Partial<ChannelsState[K]>) => void;
  audienceTotal: number;
  /** Variables insertable in every channel's content. */
  variables: NoticeVariable[];
  /** Language the notice content is written in — drives resolved counters. */
  contentLang: 'fr' | 'en';
  /** Everything the AI assistant needs to draft and review content. */
  aiContext: AiContext;
}
export function ChannelEditor({
  channels,
  onToggle,
  onUpdate,
  audienceTotal,
  variables,
  contentLang,
  aiContext
}: ChannelEditorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  /** Tip whose target text is currently highlighted in an editor. */
  const [activeTip, setActiveTip] = useState<AiTip | null>(null);
  const selectTip = (tip: AiTip) => {
    if (!tip.highlight) return;
    setActiveTip(prev => prev?.id === tip.id ? null : tip);
  };

  /** Highlight to hand to a channel's main editor, if a tip points at it. */
  const highlightFor = (key: keyof ChannelsState) => activeTip && activeTip.id.startsWith(`${key}.`) ? activeTip.highlight : null;

  /** Drafts the whole channel — offered from its main editor while empty. */
  const generate = (key: keyof ChannelsState) => {
    onUpdate(key, generateForChannel(key, aiContext) as never);
    setActiveTip(null);
  };

  /** Generation handler for a channel's main editor, only while it is empty. */
  const generateFor = (key: keyof ChannelsState) => isChannelEmpty(key, channels) ? () => generate(key) : undefined;
  const card = (key: keyof ChannelsState, Icon: typeof MailIcon, title: string, subtitle: string, reachPercent: number, reachTone: 'good' | 'medium', body: React.ReactNode) => {
    const active = channels[key].active;
    const reach = getReach(audienceTotal, reachPercent, locale);
    return <div className="bg-white rounded-xl border-[0.5px] border-slate-200 overflow-hidden" data-id="element-3831" data-mp="j9wncr7ngn4y">
        <div className="flex items-center justify-between p-4 bg-white gap-4" data-id="element-3832" data-mp="1znmxofpd47n1">
          <div className="flex items-center gap-4 min-w-0" data-id="element-3833" data-mp="1uplcnp8dly8x">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-50 text-slate-400'}`} data-id="element-3834" data-mp="1up3bce6uwtu5">
              <Icon className="w-5 h-5" data-id="element-3835" data-mp="e6nibdobq1id" />
            </div>
            <div className="min-w-0" data-id="element-3836" data-mp="e53zpgf73edf">
              <h3 className="text-base font-medium text-slate-900" data-id="element-3837" data-mp="i7qxrz2glvj2">{title}</h3>
              <p className="text-sm text-slate-500 truncate" data-id="element-3838" data-mp="i5nxmm7lem0m">{subtitle}</p>
            </div>
            <div className="ml-4 hidden md:flex items-center gap-2 flex-shrink-0" data-id="element-3839" data-mp="n4zfxznlxzkb">
              <span className="text-sm text-slate-600" data-id="element-3840" data-mp="s4c2skehb6mw">
                {reach.count} {t('citoyens', 'citizens')}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${reachTone === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`} data-id="element-3841" data-mp="11vzy4we5p7tn">
                {reach.percent} % {t('de portée', 'reach')}
              </span>
            </div>
          </div>
          <Switch checked={active} onChange={() => onToggle(key)} label={title} data-id="element-3842" data-mp="2qekno2rcvqaq" />
        </div>
        <AnimatePresence data-id="element-3843" data-mp="2mg4tnewuxhnt">
          {active && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-3844" data-mp="2hfpimu01sm90">
              <div className="p-6 pt-4 border-t border-slate-100 space-y-4 bg-slate-50/50" data-id="element-3845" data-mp="35c3b91sarxgl">
                {body}

                <AiTips tips={getChannelTips(key, aiContext)} activeTipId={activeTip?.id ?? null} onSelect={selectTip} data-id="element-3846" data-mp="30d0fk7y7j3la" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <section className="space-y-6" data-id="element-3847" data-mp="fumsgixkutcy">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3848" data-mp="fvo3inelffuo">
        {t('Canaux de diffusion', 'Distribution channels')}
      </h2>
      <div className="space-y-4" data-id="element-3849" data-mp="71ji4ji8awmj">
        {/* EMAIL */}
        {card('email', MailIcon, t('Courriel', 'Email'), t("Lettre d'information détaillée", 'Detailed newsletter'), 82, 'good', <>
            <div data-id="element-3850" data-mp="3taawqsge0eau">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3851" data-mp="21x262b0n2a1">
                {t('Objet du courriel', 'Email subject')}
              </label>
              <RichTextEditor value={channels.email.subject} onChange={subject => onUpdate('email', {
            subject
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t('Objet du courriel', 'Email subject')} placeholder={t('Objet…', 'Subject…')} data-id="element-3852" data-mp="3k94jcs91bjlt" />
            </div>

            <div data-id="element-3853" data-mp="32y7kv4rg2fyg">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3854" data-mp="32yr3yostzae2">
                {t('Contenu', 'Content')}
              </label>
              <RichTextEditor value={channels.email.body} onChange={body => onUpdate('email', {
            body
          })} minHeight={180} allowImages variables={variables} highlight={highlightFor('email')} onGenerate={generateFor('email')} ariaLabel={t('Contenu du courriel', 'Email content')} placeholder={t('Rédigez le contenu détaillé ici…', 'Write the detailed content here…')} data-id="element-3855" data-mp="3gr6x8xvxju1s" />
            </div>

            <AttachmentPicker attachments={channels.email.attachments} onChange={attachments => onUpdate('email', {
          attachments
        })} data-id="element-3856" data-mp="25az2vjqnjp17" />
          </>)}

        {/* SMS */}
        {card('sms', MessageSquareIcon, t('SMS', 'SMS'), t('Message texte court et urgent', 'Short, urgent text message'), 95, 'good', <div data-id="element-3857" data-mp="1oa31s6eppyv2">
            <div className="flex justify-between items-end mb-1" data-id="element-3858" data-mp="1t7390knat7nr">
              <label className="block text-sm font-medium text-slate-700" data-id="element-3859" data-mp="1t6bd18psh7wy">
                {t('Message SMS', 'SMS message')}
              </label>
              <span className={`text-xs ${resolvedLength(channels.sms.text, variables, contentLang) > 160 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3860" data-mp="10i5umsm7jv0a">
                {resolvedLength(channels.sms.text, variables, contentLang)} / 160
              </span>
            </div>
            <RichTextEditor value={channels.sms.text} onChange={text => onUpdate('sms', {
          text
        })} minHeight={72} variablesOnly variables={variables} highlight={highlightFor('sms')} onGenerate={generateFor('sms')} ariaLabel={t('Message SMS', 'SMS message')} placeholder={t('Message texte court…', 'Short text message…')} data-id="element-3861" data-mp="2lvs0dryjxymb" />
            <p className="mt-1.5 text-xs text-slate-400" data-id="element-3862" data-mp="3roddpc10ewhe">
              {t('Le compteur reflète la longueur finale, variables remplacées.', 'The counter reflects the final length, with variables replaced.')}
            </p>
          </div>)}

        {/* VOICE */}
        {card('voice', PhoneIcon, t('Voix automatisée', 'Automated voice'), t('Appel téléphonique généré par IA', 'AI-generated phone call'), 45, 'medium', <>
            <div data-id="element-3863" data-mp="16y5zhtickgck">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3864" data-mp="16zqufk8kq1ay">
                {t('Voix', 'Voice')}
              </label>
              <select value={channels.voice.voice} onChange={e => onUpdate('voice', {
            voice: e.target.value
          })} className="block w-full bg-white border border-slate-200 px-3 py-2 text-sm rounded-md focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-3865" data-mp="2b7809qrsrvwb">
                <option value="femme" data-id="element-3866" data-mp="1se45g22ufd04">
                  {t('Voix féminine (Standard)', 'Female voice (Standard)')}
                </option>
                <option value="homme" data-id="element-3867" data-mp="1nh3vvh6sbs49">{t('Voix masculine', 'Male voice')}</option>
              </select>
            </div>
            <div data-id="element-3868" data-mp="2269vv7dg5md7">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3869" data-mp="1xe09g1bjj4fo">
                {t("Script de l'appel", 'Call script')}
              </label>
              <RichTextEditor value={channels.voice.script} onChange={script => onUpdate('voice', {
            script
          })} minHeight={80} variablesOnly variables={variables} highlight={highlightFor('voice')} onGenerate={generateFor('voice')} ariaLabel={t("Script de l'appel", 'Call script')} placeholder={t('Bonjour, ceci est un message important de la ville…', 'Hello, this is an important message from the city…')} data-id="element-3870" data-mp="19mbbo5fy9nrc" />
            </div>
          </>)}

        {/* PUSH */}
        {card('push', BellIcon, t('Notification Push', 'Push notification'), t("Alerte sur l'application mobile", 'Alert in the mobile app'), 88, 'good', <>
            <div data-id="element-3871" data-mp="3u5w6mnsiimgh">
              <div className="flex justify-between items-end mb-1" data-id="element-3872" data-mp="2en2j3zeqnvto">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3873" data-mp="2elrfq0p7gq33">
                  {t('Titre de la notification', 'Notification title')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.title, variables, contentLang) > 50 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3874" data-mp="p4m3mdc13v3k">
                  {resolvedLength(channels.push.title, variables, contentLang)} / 50
                </span>
              </div>
              <RichTextEditor value={channels.push.title} onChange={title => onUpdate('push', {
            title
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('push')} onGenerate={generateFor('push')} ariaLabel={t('Titre de la notification', 'Notification title')} placeholder={t('Titre court…', 'Short title…')} data-id="element-3875" data-mp="6g7gw28qv38k" />
            </div>
            <div data-id="element-3876" data-mp="1ud6t6ywmvk5o">
              <div className="flex justify-between items-end mb-1" data-id="element-3877" data-mp="1udqcaiy0sela">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3878" data-mp="1ubdx2djncibx">
                  {t('Message', 'Message')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.message, variables, contentLang) > 100 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3879" data-mp="2358rorn56qdb">
                  {resolvedLength(channels.push.message, variables, contentLang)} / 100
                </span>
              </div>
              <RichTextEditor value={channels.push.message} onChange={message => onUpdate('push', {
            message
          })} minHeight={56} variablesOnly variables={variables} ariaLabel={t('Message', 'Message')} placeholder={t("Détails de l'alerte…", 'Alert details…')} data-id="element-3880" data-mp="1ke9865yf28rf" />
            </div>
          </>)}

        {/* WEBSITE */}
        {card('website', GlobeIcon, t('Site web', 'Website'), t("Bannière d'alerte sur le portail", 'Alert banner on the portal'), 100, 'good', <>
            <div data-id="element-3881" data-mp="hlwxyeiubbu0">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3882" data-mp="hldeuuhgehee">
                {t('Texte de la bannière', 'Banner text')}
              </label>
              <RichTextEditor value={channels.website.bannerText} onChange={bannerText => onUpdate('website', {
            bannerText
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('website')} onGenerate={generateFor('website')} ariaLabel={t('Texte de la bannière', 'Banner text')} placeholder={t('Alerte importante…', 'Important alert…')} data-id="element-3883" data-mp="rkm63fkxphx2" />
            </div>
            <div data-id="element-3884" data-mp="1txnjra10yo2h">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3885" data-mp="1tv1g3lh98spj">
                {t("Label du bouton d'action", 'Action button label')}
              </label>
              <RichTextEditor value={channels.website.ctaLabel} onChange={ctaLabel => onUpdate('website', {
            ctaLabel
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t("Label du bouton d'action", 'Action button label')} placeholder={t('En savoir plus', 'Learn more')} data-id="element-3886" data-mp="1jy6kbacd96ey" />
            </div>
          </>)}

        {/* SOCIAL */}
        {card('social', Share2Icon, t('Réseaux sociaux', 'Social media'), t('Publication Facebook et X', 'Facebook and X post'), 60, 'good', <>
            <div data-id="element-3887" data-mp="waydgnzahpat">
              <div className="flex justify-between items-end mb-1" data-id="element-3888" data-mp="2yviutk3m6fih">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3889" data-mp="2yxvk6ibpbs5k">
                  {t('Texte de la publication', 'Post text')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-3890" data-mp="2p70x5bqmsoj2">
                  {resolvedLength(channels.social.postText, variables, contentLang)}{' '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.social.postText} onChange={postText => onUpdate('social', {
            postText
          })} minHeight={130} allowImages variables={variables} highlight={highlightFor('social')} onGenerate={generateFor('social')} ariaLabel={t('Texte de la publication', 'Post text')} placeholder={t('Quoi de neuf ?', "What's new?")} data-id="element-3891" data-mp="3hsk9xxqvtw05" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-3892" data-mp="1si335vqpgumy">
                {t('Les publications avec image obtiennent en moyenne 2× plus d’interactions.', 'Posts with an image get on average 2× more engagement.')}
              </p>
            </div>

            <div data-id="element-3893" data-mp="lp2pr1fbwufd">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3894" data-mp="lpm8ulgptouz">
                {t('Suggestions de hashtags', 'Hashtag suggestions')}
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-3895" data-mp="qqiaesdlgyx8">
                {HASHTAG_SUGGESTIONS.map(tag => <button key={tag} type="button" onClick={() => onUpdate('social', {
              postText: `${channels.social.postText}${channels.social.postText ? ' ' : ''}${tag}`
            })} className="px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-600 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-3896" data-mp="zouadl0cp92c">
                    {tag}
                  </button>)}
              </div>
            </div>
          </>)}
      </div>
    </section>;
}